// frontend/src/CriarSuperAdmin.js
import React, { useState, useEffect } from 'react';
import { FaUserCog, FaEdit, FaTrash, FaPause, FaPlay, FaTimes, FaSave } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || '';

function CriarSuperAdmin() {
  const [formData, setFormData] = useState({
    nome_completo: '',
    email: '',
    senha: '',
    confirmar_senha: ''
  });
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLista, setLoadingLista] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Estados para edição
  const [editandoId, setEditandoId] = useState(null);
  const [editForm, setEditForm] = useState({
    nome_completo: '',
    email: '',
    senha: ''
  });

  const token = localStorage.getItem('token');

  // ============================================
  // BUSCAR SUPER ADMINS
  // ============================================
  const fetchSuperAdmins = async () => {
    setLoadingLista(true);
    try {
      const response = await fetch(`${API_URL}/api/usuarios/super-admins`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao buscar Super Admins');
      }

      const data = await response.json();
      setSuperAdmins(data);
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message);
    } finally {
      setLoadingLista(false);
    }
  };

  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  // ============================================
  // CRIAR SUPER ADMIN
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (formData.senha !== formData.confirmar_senha) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (formData.senha.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/usuarios`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nome_completo: formData.nome_completo,
          email: formData.email,
          senha: formData.senha,
          role: 'SUPER_ADMIN',
          perfil: 'SUPER_ADMIN',
          ativo: true
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao criar Super Admin');
      }

      setSuccess('✅ Super Admin criado com sucesso!');
      setFormData({
        nome_completo: '',
        email: '',
        senha: '',
        confirmar_senha: ''
      });

      fetchSuperAdmins(); // Recarregar lista
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EDITAR SUPER ADMIN
  // ============================================
  const handleEditar = (admin) => {
    setEditandoId(admin.id);
    setEditForm({
      nome_completo: admin.nome_completo,
      email: admin.email,
      senha: ''
    });
  };

  const handleSalvarEdicao = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        nome_completo: editForm.nome_completo,
        email: editForm.email
      };

      if (editForm.senha) {
        payload.senha = editForm.senha;
      }

      const response = await fetch(`${API_URL}/api/usuarios/${editandoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erro ao editar');
      }

      setSuccess('✅ Super Admin atualizado!');
      setEditandoId(null);
      setEditForm({ nome_completo: '', email: '', senha: '' });
      fetchSuperAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // PAUSAR/ATIVAR SUPER ADMIN
  // ============================================
  const handleToggleAtivo = async (admin) => {
    if (!window.confirm(`Deseja ${admin.ativo ? 'pausar' : 'ativar'} ${admin.nome_completo}?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/usuarios/${admin.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          ativo: !admin.ativo,
          nome_completo: admin.nome_completo,
          role: admin.role
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar');
      }

      setSuccess(`✅ Super Admin ${admin.ativo ? 'pausado' : 'ativado'}!`);
      fetchSuperAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  // ============================================
  // EXCLUIR SUPER ADMIN
  // ============================================
  const handleExcluir = async (admin) => {
    if (!window.confirm(`⚠️ ATENÇÃO! Deseja realmente EXCLUIR ${admin.nome_completo}? Esta ação não pode ser desfeita!`)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/usuarios/${admin.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Erro ao excluir');
      }

      setSuccess('✅ Super Admin excluído!');
      fetchSuperAdmins();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <FaUserCog style={{ color: '#6f42c1' }} /> Gerenciar Super Admins
      </h2>
      <p style={{ color: '#6c757d', marginBottom: '20px' }}>
        Apenas Super Administradores podem gerenciar outros Super Admins.
      </p>

      {error && (
        <div style={{ 
          backgroundColor: '#ffebee', padding: '10px', borderRadius: '4px', 
          color: '#c62828', marginBottom: '15px' 
        }}>
          ❌ {error}
          <button 
            onClick={() => setError('')} 
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <FaTimes />
          </button>
        </div>
      )}

      {success && (
        <div style={{ 
          backgroundColor: '#e8f5e9', padding: '10px', borderRadius: '4px', 
          color: '#2e7d32', marginBottom: '15px' 
        }}>
          {success}
        </div>
      )}

      {/* ============================================ */}
      {/* FORMULÁRIO DE CRIAÇÃO */}
      {/* ============================================ */}
      <form onSubmit={handleSubmit} style={{ 
        backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '30px'
      }}>
        <h3 style={{ marginTop: 0 }}>➕ Criar Novo Super Admin</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Nome Completo *
            </label>
            <input
              type="text"
              value={formData.nome_completo}
              onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Senha *
            </label>
            <div style={{ display: 'flex', gap: '5px' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.senha}
                onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                required
                minLength={6}
                style={{ flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ padding: '8px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', background: 'white' }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
              Confirmar Senha *
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.confirmar_senha}
              onChange={(e) => setFormData({ ...formData, confirmar_senha: e.target.value })}
              required
              minLength={6}
              style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            marginTop: '20px',
            width: '100%',
            padding: '12px',
            backgroundColor: loading ? '#6c757d' : '#6f42c1',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px'
          }}
        >
          {loading ? '⏳ Criando...' : '👑 Criar Super Admin'}
        </button>
      </form>

      {/* ============================================ */}
      {/* LISTA DE SUPER ADMINS */}
      {/* ============================================ */}
      <div style={{ 
        backgroundColor: 'white', padding: '20px', borderRadius: '8px', 
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0 }}>👑 Super Admins Cadastrados ({superAdmins.length})</h3>

        {loadingLista ? (
          <p style={{ textAlign: 'center', color: '#6c757d' }}>🔄 Carregando...</p>
        ) : superAdmins.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6c757d' }}>📭 Nenhum Super Admin cadastrado</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Nome</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {superAdmins.map((admin) => (
                  <tr key={admin.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    {editandoId === admin.id ? (
                      // Modo de edição
                      <>
                        <td style={{ padding: '10px' }}>
                          <input
                            type="text"
                            value={editForm.nome_completo}
                            onChange={(e) => setEditForm({ ...editForm, nome_completo: e.target.value })}
                            style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '10px' }}>
                          <input
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '10px' }}>
                          <input
                            type="password"
                            placeholder="Nova senha (opcional)"
                            value={editForm.senha}
                            onChange={(e) => setEditForm({ ...editForm, senha: e.target.value })}
                            style={{ width: '100%', padding: '5px', border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={handleSalvarEdicao}
                            disabled={loading}
                            style={{ 
                              padding: '5px 10px', backgroundColor: '#28a745', color: 'white', 
                              border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '5px'
                            }}
                          >
                            <FaSave />
                          </button>
                          <button
                            onClick={() => setEditandoId(null)}
                            style={{ 
                              padding: '5px 10px', backgroundColor: '#6c757d', color: 'white', 
                              border: 'none', borderRadius: '4px', cursor: 'pointer'
                            }}
                          >
                            <FaTimes />
                          </button>
                        </td>
                      </>
                    ) : (
                      // Modo de visualização
                      <>
                        <td style={{ padding: '10px' }}><strong>{admin.nome_completo}</strong></td>
                        <td style={{ padding: '10px' }}>{admin.email}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={{
                            padding: '3px 10px',
                            borderRadius: '12px',
                            backgroundColor: admin.ativo ? '#d4edda' : '#f8d7da',
                            color: admin.ativo ? '#155724' : '#721c24',
                            fontSize: '12px'
                          }}>
                            {admin.ativo ? '✅ Ativo' : '⏸️ Pausado'}
                          </span>
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button
                            onClick={() => handleEditar(admin)}
                            title="Editar"
                            style={{ 
                              padding: '5px 10px', backgroundColor: '#007bff', color: 'white', 
                              border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '5px'
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => handleToggleAtivo(admin)}
                            title={admin.ativo ? 'Pausar' : 'Ativar'}
                            style={{ 
                              padding: '5px 10px', 
                              backgroundColor: admin.ativo ? '#ffc107' : '#28a745', 
                              color: admin.ativo ? '#000' : 'white', 
                              border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '5px'
                            }}
                          >
                            {admin.ativo ? <FaPause /> : <FaPlay />}
                          </button>
                          <button
                            onClick={() => handleExcluir(admin)}
                            title="Excluir"
                            style={{ 
                              padding: '5px 10px', backgroundColor: '#dc3545', color: 'white', 
                              border: 'none', borderRadius: '4px', cursor: 'pointer'
                            }}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CriarSuperAdmin;